<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Hero Section -->
    <section class="relative py-20 bg-gradient-to-r from-[#1a2a4d] to-[#233862]">
      <div class="container mx-auto px-4">
        <div class="text-center">
          <h1 class="text-4xl md:text-5xl font-bold text-white mb-4">
            {{ currentLanguage === 'el' ? 'Προορισμοί Ακτοπλοϊκών Γραμμών' : 'Greek Island Ferry Destinations' }}
          </h1>
          <p class="text-xl text-white/80 max-w-2xl mx-auto">
            {{ currentLanguage === 'el' 
              ? 'Ανακαλύψτε και κλείστε ακτοπλοϊκά εισιτήρια για πάνω από 90 προορισμούς στα ελληνικά νησιά'
              : 'Discover and book ferry tickets to over 90 destinations across the Greek islands' }}
          </p>
        </div>

        <!-- Search and Filter -->
        <div class="mt-12 max-w-lg mx-auto">
          <div class="bg-white rounded-xl shadow-lg p-6">
            <div class="flex flex-col md:flex-row gap-4">
              <div class="flex-1">
                <input
                  v-model="searchQuery"
                  type="text"
                  :placeholder="currentLanguage === 'el' ? 'Αναζήτηση προορισμών...' : 'Search destinations...'"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5F00] focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Destinations Grid -->
    <section class="py-16">
      <div class="container mx-auto px-4">
        <div v-if="filteredDestinations.length === 0" class="text-center py-12">
          <p class="text-gray-600 text-lg">
            {{ currentLanguage === 'el' ? 'Δεν βρέθηκαν προορισμοί που να ταιριάζουν με τα κριτήριά σας.' : 'No destinations found matching your criteria.' }}
          </p>
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <router-link
            v-for="destination in filteredDestinations"
            :key="destination.id"
            :to="{ name: getRouteName('destination'), params: { id: destination.id } }"
            class="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group"
          >
            <!-- Destination Image -->
            <div class="relative h-64 overflow-hidden">
              <div
                class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent z-10"
              ></div>
              <img
                :src="destination.image"
                :alt="destination.name"
                class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <h3 class="absolute bottom-4 left-4 text-white text-2xl font-bold z-20">
                {{ destination.name }}
              </h3>
            </div>

            <!-- Routes Summary -->
            <div class="p-6">
              <div class="space-y-3">
                <div
                  v-for="route in destination.routes"
                  :key="route.from"
                  class="flex items-center justify-between"
                >
                  <div class="flex items-center space-x-2">
                    <svg
                      class="w-5 h-5 text-[#FF5F00]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 0118 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span class="text-gray-600">
                      {{ currentLanguage === 'el' ? 'Από ' : 'From ' }}{{ route.from }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="mt-4 pt-4 border-t border-gray-100">
                <div class="flex items-center justify-between">
                  <div class="text-gray-500 text-sm">
                    {{ destination.routes.length }} 
                    {{ currentLanguage === 'el' ? 'διαθέσιμα δρομολόγια' : 'routes available' }}
                  </div>
                </div>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Popular Routes Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <h2 class="text-3xl font-bold text-[#233862] mb-8 text-center">
          {{ currentLanguage === 'el' ? 'Δημοφιλείς Ακτοπλοϊκές Γραμμές' : 'Popular Ferry Routes' }}
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div
            v-for="route in popularRoutes"
            :key="route.from + route.to"
            class="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow"
          >
            <div class="flex items-center justify-between mb-4">
              <div>
                <div class="text-sm text-[#FF5F00]">
                  {{ currentLanguage === 'el' ? 'Από' : 'From' }}
                </div>
                <div class="font-medium">{{ route.from }}</div>
              </div>
              <div class="text-[#FF5F00]">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </div>
              <div>
                <div class="text-sm text-[#FF5F00]">
                  {{ currentLanguage === 'el' ? 'Προς' : 'To' }}
                </div>
                <div class="font-medium">{{ route.to }}</div>
              </div>
            </div>
            <div class="text-sm text-gray-600">
              {{ route.frequency }}
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import destinationsEn from '@/data/destinations.json'
import destinationsEl from '@/data/destinations.el.json'

const route = useRoute()
const searchQuery = ref('')

// Compute current language from route
const currentLanguage = computed(() => {
  return route.path.includes('/el/') ? 'el' : 'en'
})

// Compute route names with language prefix
const getRouteName = (baseName) => {
  return currentLanguage.value === 'el' ? `el${baseName}` : baseName
}

const destinations = computed(() => 
  currentLanguage.value === 'el' ? destinationsEl : destinationsEn
)

// Import all images
const images = import.meta.glob('@/assets/images/destinations/*.webp', { eager: true })

// Function to get the image URL
const getImageUrl = (imagePath) => {
  try {
    // Remove the leading /src if it exists
    const cleanPath = imagePath.replace(/^\/src/, '')
    const key = Object.keys(images).find(key => key.includes(cleanPath.split('/').pop()))
    return key ? images[key].default : ''
  } catch (error) {
    console.error('Error loading image:', error)
    return '' // Return empty string or a default image path
  }
}

const filteredDestinations = computed(() => {
  if (!destinations.value?.destinations) return []
  
  return destinations.value.destinations.filter((destination) => {
    // Search filter
    const matchesSearch =
      searchQuery.value === '' ||
      destination.name.toLowerCase().includes(searchQuery.value.toLowerCase())
    
    // Add image URL to destination
    destination.image = getImageUrl(destination.image)
    
    return matchesSearch
  })
})

const popularRoutes = computed(() => [
  { 
    from: currentLanguage.value === 'el' ? 'Πειραιάς' : 'Piraeus', 
    to: currentLanguage.value === 'el' ? 'Μύκονος' : 'Mykonos', 
    frequency: currentLanguage.value === 'el' ? 'Καθημερινά' : 'Daily' 
  },
  { 
    from: currentLanguage.value === 'el' ? 'Ραφήνα' : 'Rafina', 
    to: currentLanguage.value === 'el' ? 'Τήνος' : 'Tinos', 
    frequency: currentLanguage.value === 'el' ? 'Καθημερινά' : 'Daily' 
  },
  { 
    from: currentLanguage.value === 'el' ? 'Μύκονος' : 'Mykonos', 
    to: currentLanguage.value === 'el' ? 'Νάξος' : 'Naxos', 
    frequency: currentLanguage.value === 'el' ? 'Καθημερινά (Καλοκαίρι)' : 'Daily (Summer)' 
  }
])

function updateSEO(dest) {
  const title = currentLanguage.value === 'el'
    ? 'Ακτοπλοϊκά Εισιτήρια για Ελληνικά Νησιά | Κράτηση Online'
    : 'Greek Island Ferry Tickets | Book Online'
  
  const description = currentLanguage.value === 'el'
    ? 'Κλείστε ακτοπλοϊκά εισιτήρια online για όλους τους προορισμούς στην Ελλάδα. Βρείτε τα καλύτερα δρομολόγια για Μύκονο, Σαντορίνη, Κρήτη και άλλα νησιά.'
    : 'Book ferry tickets online for all destinations in Greece. Find the best routes to Mykonos, Santorini, Crete, and other islands.'

  document.title = title
  const metaDesc = document.querySelector('meta[name="description"]')
  if (metaDesc) {
    metaDesc.setAttribute('content', description)
  }
}

onMounted(() => {
  if (destinations.value) {
    updateSEO(destinations.value.destinations[0])
  }
})
</script>
